/**
 * Single page pay link checkout
 * Used for Fixed, Custom amount, Single-select list pay links
 */

import { useContext, useRef, useEffect, useState, useCallback } from "preact/hooks";
import { route } from "preact-router";

import { Configs } from "../../helpers/context";
import {
  getWalletCollectOptions,
  getEcommerceTransactionOptions,
  WALLET_COLLECT_OPTIONS,
  validateOrder,
} from "../../helpers/collect";
import { formatIntegerCurrencyDecimal } from "../../helpers/formatters";
import {
  EMIT_SUCCESS,
  GOOGLE_PAY_BUTTON_CLICK,
  APPLE_PAY_BUTTON_CLICK,
  APPLE_PAY_QR_BUTTON_CLICK,
  PAZE_BUTTON_CLICK,
  APPLE_PAY_CLOSE,
  GOOGLE_PAY_CLOSE,
  PAZE_CLOSE,
  GOOGLE_PAY_ERROR,
  APPLE_PAY_ERROR,
  PAZE_ERROR,
} from "../../helpers/event-definitions";
import { getOrderTotal } from "../../helpers/order";

import { charge } from "../../rest/charge";
import { calculateSurchargeFee } from "../../rest/fees";

const WalletCollect = ({
  payLink,
  order,
  product,
  isWorldpay,
  setTransaction,
  setSurchargeData,
  setShowSurchargeConfirmation,
  onCollectError,
  showError,
  setCharging,
  trackCustomEvent,
  paymentRequestIdToPay = '',
}) => {
  const configsContext = useContext(Configs);

  const paymentRequestIdRef = useRef(paymentRequestIdToPay);

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isDemoMode = urlParams.get("isSampleInvoice") === "true";

  // use a ref for permanent component state
  // that shouldn't trigger rerender on change
  const collectRef = useRef();

  const selectedPaymentRequest = (payLink?.invoice?.paymentRequests || []).find(
    pr => pr?.paymentRequestId === paymentRequestIdToPay
  )

  const startSession = (source, walletUpdate) => {
    if (source === 'google_pay') {
      collectRef?.current?.startGooglePaySession(walletUpdate);
      return;
    }

    collectRef?.current?.startApplePaySession(walletUpdate);
  }

  /**
   * Triggered when payment is authorized (aka nonce is ready)
   * @param {Event} event
   */
  const onPaymentAuthorized = useCallback(async (event) => {
    try {
      // if nonce not returned, fail
      if (!event.nonce) {
        throw new Error();
      }

      showError(false);
      setCharging(true);

      // attach businessId
      event.businessId = configsContext?.configs?.businessId;

      const chargeOptions = {
        ...getEcommerceTransactionOptions(
          configsContext?.configs,
          payLink,
          order,
          product,
          event,
          {
            paymentRequestIdToPay: paymentRequestIdRef.current
          }),
        nonce: event.nonce,
        emailAddress: event.shippingAddress?.emailAddress || event.billingAddress?.emailAddress,
        phone: event.shippingAddress?.phoneNumber || event.billingAddress?.phoneNumber,
        isPayableDomain: window.location.href.includes('//pay.'),
        paymentRequestIdToPay: paymentRequestIdRef.current
      };

      // calculate surcharge fee
      if (!isWorldpay && payLink.isSurchargeFeatureEnabled) {
        const surchargeFee = await calculateSurchargeFee(configsContext?.configs?.env, chargeOptions);

        if (surchargeFee?.amount > 0) {
          setSurchargeData({ surchargeFee, chargeOptions, event });
          setShowSurchargeConfirmation(true);

          setCharging(false);

          event.complete();

          return;
        }
      }

      // create transaction
      const transaction = await charge(configsContext?.configs?.env, chargeOptions);

      // Metrics.track(MetricEvents.ORDER_COMPLETED_POYNT_APPLE_PAY_IMPRESSION);
      event.complete();

      setCharging(false);
      setTransaction(transaction);

      if (trackCustomEvent) {
        trackCustomEvent(EMIT_SUCCESS, { godaddyShopperId: window?.payLink?.godaddyShopperId });
      }
      route("/transaction-complete");
    } catch (error) {
      console.log("payment authorize error", error);

      if (trackCustomEvent) {
        if (event.source === 'google_pay') {
          trackCustomEvent(GOOGLE_PAY_ERROR);
        } else if (event.source === 'apple_pay') {
          trackCustomEvent(APPLE_PAY_ERROR);
        } else if (event.source === 'paze') {
          trackCustomEvent(PAZE_ERROR);
        }
      }

      const walletError = {
        code: "invalid_payment_data",
        message: "Unable to process payment",
      };

      event.complete({ error: walletError });

      setCharging(false);

      showError("Transaction failed. Please try again.");
    }
  }, []);

  /**
   * Triggers on wallet button click
   * @param {Event} event
   */
  function onWalletButtonClick(event) {
    if (trackCustomEvent) {
      if (event.source === 'google_pay') {
        trackCustomEvent(GOOGLE_PAY_BUTTON_CLICK);
      } else if (event.source === 'apple_pay') {
        if (event.isApplePayQr) {
          trackCustomEvent(APPLE_PAY_QR_BUTTON_CLICK);
        } else {
          trackCustomEvent(APPLE_PAY_BUTTON_CLICK);
        }
      } else if (event.source === 'paze') {
        trackCustomEvent(PAZE_BUTTON_CLICK);
      }
    }
  }

  /**
   * Triggers on wallet close
   * @param {Event} event
   */
  function onCloseWallet(event) {
    if (trackCustomEvent) {
      if (event.source === 'google_pay') {
        trackCustomEvent(GOOGLE_PAY_CLOSE);
      } else if (event.source === 'apple_pay') {
        trackCustomEvent(APPLE_PAY_CLOSE);
      } else if (event.source === 'paze') {
        trackCustomEvent(PAZE_CLOSE);
      }
    }
  }

  /**
   * Triggers on wallet pay button click
   * @param {Event} event
   */
  function onWalletPayClick(e) {
    const totalPaymentRequestAmount = selectedPaymentRequest?.amount + order.tip.value;
    const amountToPay = selectedPaymentRequest?.amount
      ? formatIntegerCurrencyDecimal(totalPaymentRequestAmount)
      : formatIntegerCurrencyDecimal(getOrderTotal(order));
    const walletUpdate = {
      total: {
        label: "TOTAL",
        amount: amountToPay,
      },
    };

    const error = validateOrder(configsContext?.configs, payLink, order);
    if (error) {
      showError(error);
      throw new Error(error);
    }

    try{
      startSession(e.source, walletUpdate);
    } catch (error) {
      console.log(`session error - ${e.source}`, error);
    }
  }

  /**
   * Triggers on shipping address/method change 
   * @param {Event} event
   */
  function onShippingAddressChange(e) {
    const options = {
      shippingMethods: [],
    }

    e.updateWith(options);
  }

  useEffect(() => {
    const collect = new TokenizeJs(
      {
        businessId: configsContext?.configs?.businessId,
        storeId: configsContext?.configs?.storeId,
        applicationId: payLink.applicationId,
      },
      getWalletCollectOptions(configsContext?.configs, payLink, order),
    );

    collect.on("payment_authorized", onPaymentAuthorized);
    collect.on("shipping_address_change", onShippingAddressChange);
    collect.on("error", onCollectError);
    collect.on("wallet_button_click", onWalletButtonClick);
    collect.on("close_wallet", onCloseWallet);

    collectRef.current = collect;

    (async () => {
      try {
        // get what wallet providers are supported
        const result = await collect.supportWalletPayments();

        console.log("wallet support", result);

        // update global config with supportGooglePay since other pages use it for styling
        if (result?.googlePay) {
          configsContext?.setConfig("supportGooglePay", true);
          WALLET_COLLECT_OPTIONS.paymentMethods.push("google_pay");
        }

        // verify domain registration only when apple pay is supported
        if (result?.applePay) {
          // update global config with supportApplePay since other pages use it for styling
          configsContext?.setConfig("supportApplePay", true);
          WALLET_COLLECT_OPTIONS.paymentMethods.push("apple_pay");
        }

        if (WALLET_COLLECT_OPTIONS.paymentMethods.length) {
          collect.mount("wallet-payments-container", document, {
            ...WALLET_COLLECT_OPTIONS,
            buttonOptions: {
              ...WALLET_COLLECT_OPTIONS.buttonOptions,
              onClick: onWalletPayClick,
              type: WALLET_COLLECT_OPTIONS.paymentMethods.length > 1 ? "plain" : "pay",
              ...(isDemoMode ? { disabled: true } : {}),
            },
          });
        }
      } catch (err) {
        // do nothing, just don't turn on apple pay
        console.warn("Error checking wallet support", err);
      }
    })();

    return () => {
      collect.off?.("payment_authorized", onPaymentAuthorized);
    };
  }, [onPaymentAuthorized]);

  return <div id="wallet-payments-container" />;
};

export default WalletCollect;
